import React, { useEffect, useState, useRef } from "react";
import "./styles/PacientesInfo.css"; // Importa o CSS para estilização
import { FaUser, FaCamera, FaTrash } from "react-icons/fa";
import { useNotification } from "../../components/NotificationContext";

const PacientesInfo = () => {
  const { showNotification } = useNotification();
  // Estado local para armazenar os dados do formulário
  const [formData, setFormData] = useState({
    nome: "",
    cep: "",
    rua: "",
    numeroLocalidade: "",
    bairro: "",
    cidade: "",
    estado: "",
    profileImage: "", // URL da imagem de perfil
  });
  
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);

  // Estado de carregamento e erros
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Carrega dados do localStorage ao montar o componente
  useEffect(() => {
    fetchPlaceholder();
  }, []);

  // Carrega dados armazenados no localStorage (similar ao antigo fetchPlaceholder)
  const fetchPlaceholder = () => {
    const storedData = localStorage.getItem("patientData");
    if (!storedData) {
      console.warn("Nenhum dado de usuário armazenado encontrado.");
      return;
    }

    const data = JSON.parse(storedData);
    // Preenche o form com dados do localStorage (ajuste nomes conforme seu backend)
    setFormData({
      nome: data.Name || "",
      cep: data.CEP || "",
      rua: data.rua || "",
      numeroLocalidade: data.numero_localidade || "",
      bairro: data.bairro || "",
      cidade: data.cidade || "",
      estado: data.estado || "",
      profileImage: data.profileImage || "",
    });
    
    // Se tiver imagem de perfil, define a preview
    if (data.profileImage) {
      setPreviewImage(data.profileImage);
    }
  };

  // Atualiza o estado quando o usuário digita no input
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  
  // Manipulador de imagem de perfil
  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) { // 5MB
      showNotification("A imagem é muito grande. Por favor, escolha uma imagem menor que 5MB.", "warning");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setPreviewImage(reader.result);
      // Em um cenário real, você pode fazer upload para um servidor e obter uma URL
      // Por enquanto, usaremos a representação base64 como exemplo
      setFormData(prev => ({ ...prev, profileImage: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setPreviewImage(null);
    setFormData(prev => ({ ...prev, profileImage: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Busca endereço via CEP (similar ao buscarEnderecoNoForm)
  const handleCepBlur = async () => {
    const cep = formData.cep.trim();
    if (cep.length === 8 && /^[0-9]+$/.test(cep)) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        if (data.erro) {
          showNotification("CEP inválido. Verifique e tente novamente.", "error");
          return;
        }
        // Atualiza os campos do form com os dados retornados
        setFormData((prev) => ({
          ...prev,
          rua: data.logradouro || "",
          bairro: data.bairro || "",
          cidade: data.localidade || "",
          estado: data.uf || "",
        }));
      } catch (error) {
        console.error("Erro ao buscar o CEP:", error);
        showNotification("Erro ao buscar o CEP. Tente novamente mais tarde.", "error");
      }
    } else if (cep) {
      showNotification("Formato de CEP inválido. Use apenas números com 8 dígitos.", "warning");
    }
  };

  // Envia os dados para atualizar o paciente (similar ao atualizarPaciente)
  const handleUpdatePatient = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const patientId = localStorage.getItem("patientId");
    if (!patientId) {
      setError("ID do paciente não encontrado. Faça login novamente.");
      setLoading(false);
      return;
    }

    // Mapeia os campos do form para o objeto que a API espera
    // Ajuste conforme seus campos no backend
    const fieldsToUpdate = {
      Name: formData.nome,
      CEP: formData.cep,
      rua: formData.rua,
      numero_localidade: formData.numeroLocalidade,
      bairro: formData.bairro,
      cidade: formData.cidade,
      estado: formData.estado,
      profileImage: formData.profileImage, // Adiciona a imagem de perfil
    };

    try {
      const response = await fetch(
        `https://x8ki-letl-twmt.n7.xano.io/api:g_BmLeE8/paciente/${patientId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(fieldsToUpdate),
        }
      );

      if (!response.ok) {
        throw new Error(`Erro ao atualizar paciente (status: ${response.status})`);
      }

      const data = await response.json();
      console.log("Paciente atualizado com sucesso:", data);

      // Salva no localStorage para uso futuro
      localStorage.setItem("patientData", JSON.stringify(data));
      showNotification("Informações atualizadas com sucesso!", "success");
    } catch (err) {
      console.error("Erro ao atualizar paciente:", err);
      setError("Erro ao atualizar informações. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pacientes-info">
      <h2>Suas Informações</h2>
      {loading && <div className="loading-indicator">Atualizando...</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="info-container">
        <div className="profile-image-container">
          <div 
          
            className="profile-image" 
            onClick={handleImageClick}
          >
            {previewImage ? (
              <img src={previewImage} alt="Foto de perfil" />
            ) : (
              <div className="default-avatar">
                <FaUser />
              </div>
            )}
            <div className="image-overlay">
              <FaCamera />
            </div>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageChange}
            accept="image/*"
            style={{ display: 'none' }}
          />
          {previewImage && (
            <button 
              type="button" 
              onClick={handleRemoveImage}
              className="remove-image-btn"
            >
              <FaTrash /> Remover foto
            </button>
          )}
        </div>

        <form onSubmit={handleUpdatePatient} className="info-form">
          <div className="form-group">
            <label htmlFor="nome">Nome:</label>
            <input
              type="text"
              id="nome"
              name="nome"
              value={formData.nome}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="cep">CEP:</label>
            <input
              type="text"
              id="cep"
              name="cep"
              value={formData.cep}
              onChange={handleInputChange}
              onBlur={handleCepBlur} /* Busca endereço ao sair do campo CEP */
              maxLength="8"
              placeholder="Somente números"
            />
          </div>

          <div className="form-group">
            <label htmlFor="rua">Rua:</label>
            <input
              type="text"
              id="rua"
              name="rua"
              value={formData.rua}
              onChange={handleInputChange}
              placeholder="Rua / Avenida"
            />
          </div>

          <div className="form-group">
            <label htmlFor="numeroLocalidade">Número:</label>
            <input
              type="text"
              id="numeroLocalidade"
              name="numeroLocalidade"
              value={formData.numeroLocalidade}
              onChange={handleInputChange}
              placeholder="Ex: 123"
            />
          </div>

          <div className="form-group">
            <label htmlFor="bairro">Bairro:</label>
            <input
              type="text"
              id="bairro"
              name="bairro"
              value={formData.bairro}
              onChange={handleInputChange}
              placeholder="Seu bairro"
            />
          </div>

          <div className="form-group">
            <label htmlFor="cidade">Cidade:</label>
            <input
              type="text"
              id="cidade"
              name="cidade"
              value={formData.cidade}
              onChange={handleInputChange}
              placeholder="Sua cidade"
            />
          </div>

          <div className="form-group">
            <label htmlFor="estado">Estado:</label>
            <input
              type="text"
              id="estado"
              name="estado"
              value={formData.estado}
              onChange={handleInputChange}
              maxLength="2"
              placeholder="UF"
            />
          </div>

          <div className="form-group submit-group">
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? "Atualizando..." : "Salvar Alterações"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PacientesInfo;
